import FieldSourceCache from './FieldSourceCache';

//CG: subformular (UISubformDto) - stiva de randuri editabile inline, fiecare rand fiind o instanta FormControl in
//rol redus (isRow) peste layout-ul formularului copil. Randurile stau in memorie si se comit impreuna cu gazda.
//Cu MaxRows = 1 e relatie 1:1: randul apare singur, fara adaugare si fara stergere.
export default class SubformControl {
    //CG: limita implicita de randuri; oglinda lui UISubformDto.DefaultMaxRows
    static get DEFAULT_MAX_ROWS() { return 50; }

    //CG: cheia sub care randurile calatoresc in values-ul formularului gazda. Trebuie sa fie identica
    //cu UISubformDto.RowsValueKeyPrefix de pe server si nu e liber aleasa: nu poate incepe cu
    //fld_/act_/ml_ si nu poate contine "fld" nicaieri - _validateFormData e un test de substring.
    static get VALUE_KEY_PREFIX() { return 'xprows_'; }

    //CG: starile pe care le intelege serverul, vezi FormRowDto.StateConstants
    static get STATES() { return { New: 'NEW', Dirty: 'DIRTY', Clean: 'CLEAN', Deleted: 'DELETED' }; }

    constructor(options) {
        this.hostForm = options.hostForm;
        this.item = options.item;
        this.containerId = options.containerId;
        this.localization = options.localization;
        this.rootUrl = options.rootUrl;
        this.idExtConn = options.idExtConn;
        this.parentPkVal = options.parentPkVal;
        this.createRowForm = options.createRowForm;
        this.isPreview = options.isPreview === true;

        this.uniqueId = 'subform' + this._generateUUID().replaceAll('-', '');
        this.stackId = this.uniqueId + '-stack';
        this.addButtonId = this.uniqueId + '-add';
        this.emptyId = this.uniqueId + '-empty';
        this.toolbarId = this.uniqueId + '-toolbar';

        this.rows = [];
        //CG: randurile deja salvate pe care utilizatorul le-a scos din stiva; nu mai au reprezentare
        //vizuala, dar stergerea lor din baza pleaca odata cu salvarea formularului gazda
        this.deletedRows = [];
        this.formRuntime = null;
        this.rowLayout = null;
        this.isLoaded = false;

        //CG: pornim din configurarea nodului - designerul nu are server; plicul de runtime le confirma apoi
        this.maxRows = this._getMaxRows();
        this.isSingleRow = this.maxRows == 1;
        this.createWhenEmpty = this.item.CtlProps != null && this.item.CtlProps.CreateWhenEmpty === true;

        //CG: randurile impart acelasi layout, deci si aceleasi campuri FK - fara asta fiecare rand isi
        //cerea singur fiecare sursa (N randuri x M campuri cereri identice la deschiderea formularului)
        this.fieldSourceCache = new FieldSourceCache();

        //CG: fara container nu avem unde randa; iesim tacut, ca un subformular sa nu poata rupe tot formularul
        let container = $('#' + this.containerId);
        if (container.length == 0) {
            return;
        }

        container[0][this.uniqueId] = this;

        this._ensureStyles();
        this._render();

        //CG: in designer nodul poate fi inca nesalvat, deci un apel pe Id ar da 404 la fiecare
        //reincarcare a previzualizarii; aratam doar scheletul, fara randuri
        if (!this.isPreview) {
            this._loadRuntime();
        }
    }

    //CG: culege randurile pentru salvarea impreuna cu formularul gazda; valorile sunt exact cele pe
    //care le-ar trimite formularul copil deschis in modal, deci serverul le salveaza fara traducere
    harvest() {
        let rows = [];

        this.rows.forEach(row => {
            let values = row.formControl != null ? row.formControl.harvestValues() : {};

            if (this._isSkippedBlankRow(row, values)) {
                return;
            }

            rows.push(this._harvestRow(row, values));
        });

        this.deletedRows.forEach(deleted => {
            rows.push({
                __rowId: deleted.rowId,
                __state: SubformControl.STATES.Deleted,
                __idForm: this.formRuntime != null ? this.formRuntime.Id : null,
                __pkVal: deleted.pkVal,
                values: {}
            });
        });

        return rows;
    }

    _harvestRow(row, values) {
        let state = row.state;

        //CG: un rand hidratat pleaca CLEAN si devine DIRTY doar daca valorile chiar difera de amprenta
        //luata la incarcare. Fara amprenta formularul randului nu a apucat sa se incarce, deci nici
        //utilizatorul nu avea ce modifica in el - il lasam CLEAN, altfel am suprascrie inregistrarea
        //existenta cu valorile unui formular gol.
        if (state == SubformControl.STATES.Clean && this._isRowDirty(row, values)) {
            state = SubformControl.STATES.Dirty;
        }

        return {
            __rowId: row.rowId,
            __state: state,
            __idForm: this.formRuntime != null ? this.formRuntime.Id : null,
            __pkVal: row.pkVal,
            values: values
        };
    }

    //CG: un rand e "modificat" doar daca valorile chiar difera de amprenta luata la incarcare.
    //Fara amprenta formularul nu a apucat sa se incarce, deci nici utilizatorul nu avea ce modifica in el.
    _isRowDirty(row, values) {
        if (row.formControl == null || row.baseline == null) {
            return false;
        }

        let current = values != null ? values : row.formControl.harvestValues();

        return row.baseline !== JSON.stringify(current);
    }

    //CG: pe 1:1 randul gol apare singur, deci "neatins" nu inseamna ca utilizatorul vrea o inregistrare - nu se
    //trimite si nu se valideaza, afara de cazul in care configuratorul a cerut crearea partenerului si gol
    _isSkippedBlankRow(row, values) {
        if (!this.isSingleRow || this.createWhenEmpty || row.state != SubformControl.STATES.New) {
            return false;
        }

        return !this._isRowDirty(row, values);
    }

    //CG: cheia primara a fost deja pusa la stergere de utilizator
    _isPendingDelete(pkVal) {
        return this.deletedRows.some(deleted => String(deleted.pkVal) === String(pkVal));
    }

    //CG: valideaza randurile cu aceleasi reguli ca formularul copil deschis in modal; fiecare rand isi
    //decoreaza singur campurile, aici doar agregam mesajele pentru sumarul afisat de gazda
    validate() {
        //CG: calculat o singura data - un rand sarit nu are voie sa aduca in sumar erori ramase de la o incercare anterioara
        let skipped = this.rows.map(row => row.formControl == null || this._isSkippedBlankRow(row));
        let promises = [];

        this.rows.forEach((row, i) => {
            if (skipped[i]) {
                promises.push(Promise.resolve(true));
                return;
            }

            //CG: pornim de la zero, altfel un camp corectat intre doua incercari ar ramane in sumar
            row.formControl.invalidFields = {};
            promises.push(row.formControl.validateForm());
        });

        return Promise.all(promises).then(results => {
            let invalidFields = {};
            let isValid = true;

            for (let i = 0; i < this.rows.length; i++) {
                if (skipped[i]) {
                    continue;
                }

                if (results[i] === false) {
                    isValid = false;
                }

                let formControl = this.rows[i].formControl;

                Object.keys(formControl.invalidFields).forEach(key => {
                    let invalidField = formControl.invalidFields[key];

                    //CG: randurile impart acelasi layout, deci si aceleasi Id-uri de nod - cheia trebuie
                    //sa poarte si randul, altfel mesajele a N randuri se suprascriu intre ele
                    invalidFields[this.uniqueId + '-' + i + '-' + key] = {
                        field: invalidField.field,
                        message: this._getRowPrefix(i) + invalidField.message
                    };
                });
            }

            return { isValid: isValid, invalidFields: invalidFields };
        });
    }

    getRows() {
        return this.rows;
    }

    //CG: eroarea venita de la server, pusa pe randul care a produs-o. Randul se cauta dupa rowId, nu dupa
    //index - lotul trimis contine si randurile sterse, asa ca indexul lui nu se potriveste cu stiva
    showServerError(rowError) {
        let index = this.rows.findIndex(row => row.rowId == rowError.RowId);

        //CG: un rand sters nu mai are reprezentare vizuala, deci nu avem unde pune eroarea; ramane in sumar
        if (index < 0) {
            return { message: rowError.Message, containerId: null };
        }

        let row = this.rows[index];

        if (rowError.FieldId != null && row.formControl != null && row.formControl.metadata != null) {
            //CG: FieldId vine ca "fld_<idControl>", aceeasi cheie cu care pleaca valorile spre server
            let id = String(rowError.FieldId).indexOf('fld_') == 0 ? String(rowError.FieldId).substring(4) : String(rowError.FieldId);
            let item = row.formControl._searchFieldById(row.formControl.metadata, id);

            if (item != null) {
                row.formControl._decorateField(item, false, rowError.Message);
            }
        }

        //CG: chenarul pe rand se pune si cand campul e decorat - una spune care rand, cealalta care camp
        $('#' + row.containerId + '-wrapper').addClass('xp-subform-row-error');

        return { message: this._getRowPrefix(index) + rowError.Message, containerId: row.containerId };
    }

    //CG: marcajele de la server nu se sting singure la editare, deci se sterg la fiecare noua salvare
    clearServerErrors() {
        $('#' + this.stackId).find('.xp-subform-row-error').removeClass('xp-subform-row-error');
    }

    //CG: apelat de _handleCascadedSublist cand se schimba un camp de care depinde SqlWhere-ul nodului.
    //Reincarcam metadatele (se poate schimba valoarea cheii de legatura) si rehidratam din baza; randurile
    //adaugate si inca nesalvate raman pe loc, altfel utilizatorul ar pierde ce a tastat.
    refresh() {
        //CG: s-a schimbat contextul, deci nu mai pastram nimic din ce am cerut pentru cel vechi
        this.fieldSourceCache.clear();
        this._loadRuntime(true);
    }

    destroy() {
        this.rows.forEach(row => {
            if (row.formControl != null) {
                row.formControl.destroy();
            }
        });
        this.rows = [];
        this.deletedRows = [];
        this.fieldSourceCache.clear();

        let container = $('#' + this.containerId);
        if (container.length > 0) {
            container[0].innerHTML = '';
            delete container[0][this.uniqueId];
        }
    }

    addRow() {
        if (!this.isLoaded) {
            return null;
        }

        if (this.rows.length >= this.maxRows) {
            toastr.warning(this._getResource('MaxRowsReached').replace('{MaxRows}', this.maxRows));
            return null;
        }

        //CG: rand nou, deci fara cheie primara; sir gol si nu null, ca sa nu ajunga 'null' in query
        //string-ul lui _getData, exact ca la formularul deschis in modal pentru inregistrare noua
        return this._buildRow('', SubformControl.STATES.New, null, !this.isSingleRow);
    }

    //CG: rand pentru o inregistrare deja salvata - acelasi formular copil, dar cu objectId setat, deci
    //isi incarca singur valorile prin drumul obisnuit (EntityForm/GetData), la fel ca modalul de editare
    _hydrateRow(dataRow) {
        if (!this.isLoaded || dataRow == null || dataRow.PkVal == null) {
            return null;
        }

        //CG: un rand scos de utilizator nu are voie sa reapara doar pentru ca subformularul s-a reincarcat -
        //altfel il poate sterge a doua oara si aceeasi cheie ar pleca la server ca doua randuri DELETED
        if (this._isPendingDelete(dataRow.PkVal)) {
            return null;
        }

        //CG: randurile modificate le pastreaza _clearSavedRows, deci pot veni inca o data din baza;
        //o a doua copie ar trimite doua salvari pe aceeasi inregistrare
        if (this.rows.some(row => row.pkVal != null && String(row.pkVal) === String(dataRow.PkVal))) {
            return null;
        }

        if (this.rows.length >= this.maxRows) {
            return null;
        }

        return this._buildRow(dataRow.PkVal, SubformControl.STATES.Clean, dataRow.PkVal, !this.isSingleRow && dataRow.AllowDelete !== false);
    }

    _buildRow(objectId, state, pkVal, allowDelete) {
        let rowId = this._generateUUID();
        let pageGuid = rowId.replaceAll('-', '');
        let containerId = this.uniqueId + '-row-' + pageGuid;

        $('#' + this.stackId).append(this._getRowTemplate(containerId, rowId));

        let row = { rowId: rowId, containerId: containerId, formControl: null, state: state, pkVal: pkVal, baseline: null };

        //CG: randul intra in lista inainte de loadForm, ca amprenta luata la incarcare sa aiba unde sa cada
        this.rows.push(row);

        let formControl = this.createRowForm({
            isRow: true,
            hostFormControl: this.hostForm,
            fieldSourceCache: this.fieldSourceCache,
            container: containerId,
            pageGuid: pageGuid,
            objectId: objectId,
            onBeforeFormLoaded: (form) => this.hostForm.subformCallbacks.onBeforeFormLoaded?.(form, this.item, rowId),
            onFormLoaded: (form) => {
                let pending = this.hostForm.subformCallbacks.onFormLoaded?.(form, this.item, rowId);

                //CG: daca hook-ul gazdei e asincron, amprenta se ia dupa ce termina (si cand esueaza)
                if (pending != null && typeof pending.then === 'function') {
                    return pending.finally(() => this._onRowLoaded(rowId));
                }
                this._onRowLoaded(rowId);
            },
            onBeforeFormSaved: (form, data) => this.hostForm.subformCallbacks.onBeforeFormSaved?.(form, data, this.item, rowId),
            onAfterFormSaved: (form, data, result) => this.hostForm.subformCallbacks.onAfterFormSaved?.(form, data, result, this.item, rowId),
            onMapElementSelected: (form, layer, feature, layerObject) => this.hostForm.subformCallbacks.onMapElementSelected?.(form, layer, feature, layerObject, this.item, rowId),
            onFormSublistFilterLoaded: (form, sublistId) => this.hostForm.subformCallbacks.onSublistFilterLoaded?.(form, sublistId, this.item, rowId),
            onFormMapStarted: (form, mapControl, item) => this.hostForm.subformCallbacks.onFormMapStarted?.(form, mapControl, item, this.item, rowId),
            onFormMapFeatureEdited: (form, field) => this.hostForm.subformCallbacks.onFormMapFeatureEdited?.(form, field, this.item, rowId),
            formId: this.formRuntime.Id,
            idExtConn: this.formRuntime.IdExtConn,
            layoutId: this.formRuntime.IdUILayout,
            otherParameters: this.formRuntime.OtherParameters,
            getDataUrl: this.formRuntime.GetDataUrl,
            getDataPostParam: this.formRuntime.GetDataPostParam,
            saveDataUrl: this.formRuntime.SaveDataUrl,
            saveDataPostParam: this.formRuntime.SaveDataPostParam,
            XpCommands: this.formRuntime.XpCommands,
            context: 'partial',
            isFilter: false,
            isPreview: false,
            localization: this.hostForm.localization,
            actionLocalization: this.hostForm.actionLocalization,
            generalResources: this.hostForm.generalResources,
            customFormResources: this.hostForm.customFormResources
        });

        row.formControl = formControl;

        //CG: lista de actiuni trebuie sa fie [] si niciodata null - _getActionButtons o parcurge neprotejat
        formControl.loadForm(this.rowLayout, []);

        //CG: fara drept de stergere (sau pe 1:1) butonul dispare; serverul refuza oricum stergerea,
        //dar nu are rost sa oferim o actiune care se termina in eroare
        if (allowDelete === false) {
            $('#' + containerId + '-remove').remove();
        } else {
            $('#' + containerId + '-remove').on('click', () => this.removeRow(rowId));
        }

        this._updateChrome();

        return row;
    }

    //CG: amprenta valorilor de la incarcare, luata dupa comenzile de load si de valoare implicita. Pe randurile
    //salvate deosebeste un rand doar afisat de unul modificat; pe randul gol al unui 1:1 spune daca a fost atins
    _onRowLoaded(rowId) {
        let row = this.rows.find(item => item.rowId == rowId);

        if (row == null || row.formControl == null) {
            return;
        }

        row.baseline = JSON.stringify(row.formControl.harvestValues());
    }

    removeRow(rowId) {
        let index = this.rows.findIndex(row => row.rowId == rowId);

        if (index < 0) {
            return;
        }

        let row = this.rows[index];

        //CG: un rand deja salvat nu dispare doar scotandu-l din stiva - stergerea din baza pleaca odata
        //cu salvarea formularului gazda, in aceeasi tranzactie
        if (row.pkVal != null && row.pkVal !== '' && !this._isPendingDelete(row.pkVal)) {
            this.deletedRows.push({ rowId: row.rowId, pkVal: row.pkVal });
        }

        if (row.formControl != null) {
            row.formControl.destroy();
        }

        $('#' + row.containerId + '-wrapper').remove();
        this.rows.splice(index, 1);

        this._updateChrome();
    }

    _loadRuntime(keepRows) {
        let url = this.rootUrl + 'EntityForm/SubformRuntime?idUISubform=' + this.item.Id;

        if (this.parentPkVal != null && this.parentPkVal !== '') {
            url += '&pkValParent=' + encodeURIComponent(this.parentPkVal);
        }

        if (this.idExtConn != null) {
            url += '&idExtConn=' + this.idExtConn;
        }

        $.ajax({
            type: 'GET',
            url: url,
            contentType: 'application/json; charset=utf-8',
            success: (envelope) => {
                this.formRuntime = envelope.Form;

                //CG: limita si regula 1:1 sunt ale serverului; cele din configurarea locala valorau doar pana aici
                if (envelope.MaxRows != null && envelope.MaxRows > 0) {
                    this.maxRows = envelope.MaxRows;
                }
                this.isSingleRow = envelope.IsSingleRow === true;

                //CG: formularul normal isi incarca JS-ul din view; subformularul primeste doar JSON,
                //deci il incarcam explicit, o singura data pe pagina si inainte de initializarea randurilor.
                this._loadScriptOnce(envelope.ScriptUrl).then(() => {
                    //CG: pe cascada layout-ul randului nu se schimba, dar contextul da - deci rehidratam
                    if (keepRows && this.rowLayout != null) {
                        this._loadData();
                        return;
                    }

                    this._loadLayout();
                }).catch(() => {
                    toastr.error(this._getResource('GetSublistError'));
                });
            },
            error: (e) => {
                toastr.error(e.responseText, this._getResource('GetSublistError'));
            }
        });
    }

    _loadScriptOnce(url) {
        if (url == null || url === '') {
            return Promise.resolve();
        }

        window.xpFormScriptPromises = window.xpFormScriptPromises || {};

        if (window.xpFormScriptPromises[url] != null) {
            return window.xpFormScriptPromises[url];
        }

        window.xpFormScriptPromises[url] = new Promise((resolve, reject) => {
            let script = document.createElement('script');
            script.src = url;
            script.onload = () => resolve();
            script.onerror = () => {
                delete window.xpFormScriptPromises[url];
                reject(new Error('Nu s-a putut incarca JavaScript-ul subformularului: ' + url));
            };
            document.head.appendChild(script);
        });

        return window.xpFormScriptPromises[url];
    }

    _loadLayout() {
        $.ajax({
            type: 'GET',
            url: this.rootUrl + 'UILayout/GetUILayoutRuntime?id=' + this.formRuntime.IdUILayout,
            contentType: 'application/json; charset=utf-8',
            success: (layout) => {
                this.rowLayout = layout;
                this.isLoaded = true;
                this.item.isLoaded = true;
                this._updateChrome();
                this._loadData();
            },
            error: (e) => {
                toastr.error(e.responseText, this._getResource('GetSublistError'));
            }
        });
    }

    //CG: hidratarea randurilor deja salvate. Cerem doar cheile primare - valorile si le incarca fiecare
    //rand singur, prin acelasi EntityForm/GetData pe care il foloseste si modalul de editare.
    _loadData() {
        this._clearSavedRows();

        if (this.parentPkVal == null || this.parentPkVal === '') {
            this._ensureSingleRow();
            this._updateChrome();
            return;
        }

        //CG: limita nu pleaca din client - serverul o ia de pe nod, ca un parametru forjat sa nu scoata mai multe randuri
        let url = this.rootUrl + 'EntityForm/SubformData?idUISubform=' + this.item.Id
            + '&pkValParent=' + encodeURIComponent(this.parentPkVal)
            + '&parentValues=' + encodeURIComponent(this._getParentValues());

        if (this.idExtConn != null) {
            url += '&idExtConn=' + this.idExtConn;
        }

        $.ajax({
            type: 'GET',
            url: url,
            contentType: 'application/json; charset=utf-8',
            success: (result) => {
                let dataRows = result != null && result.Rows != null ? result.Rows : [];

                dataRows.forEach(dataRow => this._hydrateRow(dataRow));

                //CG: cu mai multi copii decat incap in stiva am ascunde tacut inregistrari - mai bine o spunem;
                //pe 1:1 inseamna date care deja incalca relatia
                if (result != null && result.Total > dataRows.length) {
                    toastr.warning(this._getResource('RowsTruncated').replace('{MaxRows}', this.maxRows).replace('{Total}', result.Total));
                }

                this._ensureSingleRow();
                this._updateChrome();
            },
            error: (e) => {
                toastr.error(e.responseText, this._getResource('GetSublistError'));
            }
        });
    }

    //CG: pe 1:1 randul exista mereu - fie partenerul incarcat din baza, fie unul gol, gata de completat
    _ensureSingleRow() {
        if (this.isSingleRow && this.isLoaded && this.rows.length == 0) {
            this.addRow();
        }
    }

    //CG: la rehidratare aruncam doar randurile venite din baza si ramase neatinse; cele adaugate si inca
    //nesalvate, ca si cele hidratate pe care utilizatorul le-a modificat, raman pe loc - altfel s-ar pierde
    //tacut ce a tastat, doar pentru ca s-a schimbat un camp de care depinde subformularul
    _clearSavedRows() {
        for (let i = this.rows.length - 1; i >= 0; i--) {
            let row = this.rows[i];
            let isSaved = row.pkVal != null && row.pkVal !== '';

            //CG: pe 1:1 si randul gol neatins se reface, altfel ar ocupa singurul loc si partenerul din baza n-ar mai incapea
            if (!isSaved && !this.isSingleRow) {
                continue;
            }

            if (this._isRowDirty(row)) {
                continue;
            }

            if (row.formControl != null) {
                row.formControl.destroy();
            }

            $('#' + row.containerId + '-wrapper').remove();
            this.rows.splice(i, 1);
        }
    }

    //CG: aceleasi valori pe care le trimite si grila (ListControl.parameterMap), ca un SqlWhere cu
    //ctx.<camp> sa se rezolve identic pe sublista si pe subformular
    _getParentValues() {
        let parentValues = {
            formId: { value: this.hostForm.formId * 1, type: 'INTEGER' },
            objectId: { value: this.hostForm.objectId, type: 'STRING' }
        };

        if (this.item.DependsOnGuids != null && this.item.DependsOnGuids.length > 0) {
            this.item.DependsOnGuids.forEach(depends => {
                let dependant = this.hostForm._getItemByGuid(depends, [this.hostForm.metadata]);

                if (dependant == null) {
                    return;
                }

                let value = this.hostForm._getItemValueByGuid(depends, true);
                let tagName = dependant.Name;
                let tagNameFld = 'fld_' + dependant.Name;
                let dataType = dependant.DataType;

                if (dependant.EntityField) {
                    tagName = dependant.EntityField.FieldName;
                    dataType = dependant.EntityField.DataType;
                }

                if (this.item.SqlWhere != null && this.item.SqlWhere != '' && (this.item.SqlWhere.indexOf(tagName) >= 0 || this.item.SqlWhere.indexOf(tagNameFld) >= 0)) {
                    parentValues['fld_' + dependant.Id] = { value: value, type: dataType };
                }
            });
        }

        return JSON.stringify(parentValues);
    }

    _render() {
        $('#' + this.containerId)[0].innerHTML =
            `<div class="xp-subform-stack" id="` + this.stackId + `"></div>
             <div class="xp-subform-empty" id="` + this.emptyId + `">` + this._getResource('NoRows') + `</div>
             <div class="xp-subform-toolbar" id="` + this.toolbarId + `">
                 <button type="button" class="btn btn-sm btn-primary xp-subform-row-add" id="` + this.addButtonId + `" title="` + this._getResource('AddRow') + `" disabled="disabled">
                     <i class="fal fa-plus mr-2"></i><span>` + this._getResource('AddRow') + `</span>
                 </button>
             </div>`;

        $('#' + this.addButtonId).on('click', () => this.addRow());

        this._updateChrome();
    }

    _getRowTemplate(containerId, rowId) {
        //CG: pe 1:1 randul nu se poate sterge, deci nici butonul nu se randeaza
        let removeButton = this.isSingleRow ? '' :
            `<button type="button" class="xp-subform-row-remove" id="` + containerId + `-remove" title="` + this._getResource('RemoveRow') + `">
                <i class="fas fa-times"></i>
            </button>`;

        return `<div class="xp-subform-row" id="` + containerId + `-wrapper" data-row-id="` + rowId + `">
                    <div class="xp-form-container xp-subform-row-form" id="` + containerId + `"></div>
                    ` + removeButton + `
                </div>`;
    }

    //CG: butonul de adaugare se activeaza doar dupa ce avem layout-ul si cat timp mai incape un rand
    _updateChrome() {
        $('#' + this.containerId).toggleClass('xp-subform-single', this.isSingleRow);

        //CG: pe 1:1 nu exista nimic de adaugat - randul apare singur, ca un grup de campuri
        if (this.isSingleRow) {
            $('#' + this.toolbarId).hide();
        } else {
            $('#' + this.toolbarId).show();
            $('#' + this.addButtonId).prop('disabled', !(this.isLoaded && this.rows.length < this.maxRows));
        }

        //CG: in designer stiva ramane goala, deci placeholder-ul arata unde sta subformularul chiar si pe 1:1
        if (this.rows.length > 0 || (this.isSingleRow && !this.isPreview)) {
            $('#' + this.emptyId).hide();
        } else {
            $('#' + this.emptyId).show();
        }
    }

    //CG: prefixul de rand din mesajele de eroare; pe 1:1 e un singur rand, deci ar fi doar zgomot
    _getRowPrefix(index) {
        if (this.isSingleRow) {
            return '';
        }

        return this._getResource('RowLabel').replace('{Index}', (index + 1).toString()) + ': ';
    }

    _getMaxRows() {
        let configured = this.item.CtlProps != null ? this.item.CtlProps.MaxRows : null;
        return configured != null && configured > 0 ? configured : SubformControl.DEFAULT_MAX_ROWS;
    }

    _getResource(name) {
        if (this.localization != null && this.localization[name] != null && this.localization[name].trim() != '') {
            return this.localization[name];
        }

        return name;
    }

    _generateUUID() {
        return this.hostForm._generateUUID();
    }

    //CG: stilurile se injecteaza o singura data pe pagina, indiferent de cate subformulare exista
    _ensureStyles() {
        if (document.getElementById('xp-subform-styles') != null) {
            return;
        }

        let style = document.createElement('style');
        style.id = 'xp-subform-styles';
        style.textContent =
            '.xp-subform-row {' +
            'position:relative;margin-bottom:8px;' +
            '}' +
            //CG: chenarul sta pe formular, nu pe tot randul; delimiteaza randul si pe formularele copil
            //fara etichete, unde altfel s-ar citi ca un bloc unic
            '.xp-subform-row-form {' +
            'padding:4px;' +
            'border:1px solid rgba(0,0,0,0.08);border-radius:5px;' +
            '}' +
            //CG: pe 1:1 randul e doar o continuare a formularului gazda, deci fara chenar si fara spatiere proprie
            '.xp-subform-single .xp-subform-row {margin-bottom:0;}' +
            '.xp-subform-single .xp-subform-row-form {padding:0;border:0;}' +
            //CG: butonul de stergere sta in interiorul chenarului si apare doar cat timp randul e sub
            //mouse (sau are focus in el) - o stiva de zeci de randuri ramane curata, fara N butoane rosii.
            //Fara fundal si fara contur: orice "medalion" permanent devine zgomot repetat de N ori.
            '.xp-subform-row-remove {' +
            'position:absolute;top:2px;right:2px;z-index:2;' +
            'width:18px;height:18px;padding:0;' +
            'display:flex;align-items:center;justify-content:center;' +
            'border:0;border-radius:50%;background:transparent;' +
            'color:var(--theme-danger, #dc3545);' +
            'font-size:13px;line-height:1;cursor:pointer;' +
            'opacity:0;pointer-events:none;transition:opacity 0.12s ease-in-out;' +
            '}' +
            '.xp-subform-row:hover .xp-subform-row-remove,' +
            '.xp-subform-row:focus-within .xp-subform-row-remove {' +
            'opacity:1;pointer-events:auto;' +
            '}' +
            //CG: pe hover doar o urma de fundal, cat sa se vada ca e tinta de click
            '.xp-subform-row-remove:hover {background:rgba(220,53,69,0.12);}' +
            //CG: pe ecrane fara mouse nu exista hover, deci butonul ar fi de negasit - acolo ramane vizibil
            '@media (hover: none) {' +
            '.xp-subform-row-remove {opacity:1;pointer-events:auto;}' +
            '}' +
            //CG: randul refuzat de server - chenar rosu pe formular, ca sa se vada care e in stiva
            '.xp-subform-row-error > .xp-subform-row-form {' +
            'border-color:var(--theme-danger, #dc3545);' +
            'box-shadow:0 0 0 1px var(--theme-danger, #dc3545);' +
            '}' +
            '.xp-subform-empty {' +
            'padding:12px;margin-bottom:8px;' +
            'color:var(--theme-fusion-400);font-size:13px;' +
            'border:1px solid rgba(0,0,0,0.08);border-radius:5px;' +
            '}' +
            '.xp-subform-toolbar {margin-bottom:12px;}';

        document.head.appendChild(style);
    }
}
